'use client'

import { useState, useRef, useCallback } from 'react'
import { useTranslations } from 'next-intl'
import { Upload, X, FileText, Image, Trash2, Download, Loader2, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { apiClient, getToken, ApiError } from '@/lib/api-client'

type FileUploadTranslate = ReturnType<typeof useTranslations<'fileUpload'>>

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Attachment {
  id: string
  filename: string
  originalFilename: string
  mimeType: string
  fileSizeBytes: number
  createdAt: string
}

interface FileUploadProps {
  /** Bestehende Anhänge */
  attachments: Attachment[]
  /** z.B. "maintenance" oder "tax" */
  entityType: 'maintenance' | 'tax'
  /** ID des verknüpften Records */
  entityId: string
  /** Callback nach Upload/Löschung */
  onChanged: () => void
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024
const ALLOWED_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
])

const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? 'http://127.0.0.1:3001/api/v1'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function isImageType(mimeType: string): boolean {
  return mimeType.startsWith('image/')
}

/**
 * Clientseitige Vorab-Validierung (MIME-Typ und Größe).
 * Doppelte Absicherung – die eigentliche Validierung findet serverseitig statt.
 */
function validateFileClient(file: File, t: FileUploadTranslate): string | null {
  if (!ALLOWED_MIME_TYPES.has(file.type)) {
    return t('invalidMimeType', { mimeType: file.type })
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return t('tooLarge', { size: formatFileSize(file.size) })
  }
  return null
}

/**
 * Erzeugt eine lesbare Fehlermeldung aus einem beliebigen Fehler.
 * Gibt keine internen Details (Pfade, Stack-Traces) preis.
 */
function toUserMessage(err: unknown, t: FileUploadTranslate): string {
  if (err instanceof ApiError) {
    // Statuscode-spezifische Hinweise
    if (err.status === 401 || err.status === 403) {
      return t('unauthorized')
    }
    if (err.status === 400) {
      return err.message
    }
    if (err.status >= 500) {
      return t('serverError')
    }
    return err.message
  }
  if (err instanceof Error) {
    // Netzwerkfehler (kein Status)
    if (err.message.includes('fetch') || err.message.toLowerCase().includes('network')) {
      return t('networkError')
    }
    return err.message
  }
  return t('unknownError')
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FileUpload({ attachments, entityType, entityId, onChanged }: FileUploadProps) {
  const t = useTranslations('fileUpload')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  /** Behält die zuletzt gewählten Dateien für Retry vor */
  const [lastFiles, setLastFiles] = useState<File[] | null>(null)

  const uploadFiles = useCallback(async (files: File[]) => {
    setUploading(true)
    setError(null)

    try {
      for (const file of files) {
        // Clientseitige Vorab-Validierung
        const validationError = validateFileClient(file, t)
        if (validationError) {
          setError(validationError)
          return
        }

        const formData = new FormData()
        formData.append('file', file)

        const token = getToken()

        const res = await fetch(
          `${API_BASE}/attachments/${entityType}/${entityId}`,
          {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData,
          },
        )

        if (!res.ok) {
          const body = await res.json().catch(() => ({ error: 'Upload fehlgeschlagen' })) as {
            error?: string
          }
          const apiErr = new ApiError(res.status, body.error ?? `Fehler ${res.status}`)
          throw apiErr
        }
      }
      setLastFiles(null)
      onChanged()
    } catch (err) {
      setError(toUserMessage(err, t))
      // Dateien für Retry merken
      setLastFiles(files)
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [entityType, entityId, onChanged, t])

  const handleUpload = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return
    const fileArray = Array.from(files)
    setLastFiles(fileArray)
    await uploadFiles(fileArray)
  }, [uploadFiles])

  const handleRetry = useCallback(async () => {
    if (!lastFiles) return
    await uploadFiles(lastFiles)
  }, [lastFiles, uploadFiles])

  const handleDelete = useCallback(async (attachmentId: string) => {
    setDeletingId(attachmentId)
    setError(null)
    try {
      await apiClient.delete(`/attachments/${attachmentId}`)
      onChanged()
    } catch (err) {
      setError(toUserMessage(err, t))
    } finally {
      setDeletingId(null)
    }
  }, [onChanged, t])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    void handleUpload(e.dataTransfer.files)
  }, [handleUpload])

  return (
    <div className="space-y-3">
      {/* Error banner with optional Retry */}
      {error && (
        <div
          className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600 ring-1 ring-inset ring-red-200 dark:bg-red-900/20 dark:text-red-400 dark:ring-red-800"
          role="alert"
          aria-live="assertive"
        >
          <span className="flex-1">{error}</span>
          <div className="flex shrink-0 gap-1">
            {lastFiles && (
              <button
                onClick={() => void handleRetry()}
                disabled={uploading}
                className="flex items-center gap-1 rounded px-1.5 py-0.5 font-medium text-red-700 hover:bg-red-100 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-800/30"
                aria-label={t('ariaRetry')}
              >
                <RefreshCw className="h-3 w-3" />
                Retry
              </button>
            )}
            <button
              onClick={() => { setError(null); setLastFiles(null) }}
              className="ml-1"
              aria-label={t('ariaCloseError')}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Existing Attachments */}
      {attachments.length > 0 && (
        <div className="space-y-1.5">
          {attachments.map((att) => (
            <div
              key={att.id}
              className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2.5 shadow-sm transition-colors hover:border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600"
            >
              {isImageType(att.mimeType) ? (
                <Image className="h-4 w-4 shrink-0 text-blue-500" />
              ) : (
                <FileText className="h-4 w-4 shrink-0 text-gray-400" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-700 dark:text-gray-300">
                  {att.originalFilename}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {formatFileSize(att.fileSizeBytes)}
                </p>
              </div>
              <div className="flex shrink-0 gap-1">
                {/* Download goes through the authenticated backend proxy – never a direct S3 URL */}
                <a
                  href={`${API_BASE}/attachments/${att.id}/download`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/20 dark:hover:text-primary-400"
                  aria-label={t('ariaDownload', { filename: att.originalFilename })}
                >
                  <Download className="h-3.5 w-3.5" />
                </a>
                <button
                  onClick={() => void handleDelete(att.id)}
                  disabled={deletingId === att.id}
                  className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                  aria-label={t('ariaDelete', { filename: att.originalFilename })}
                >
                  {deletingId === att.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click() }}
        aria-label={t('ariaUpload')}
        className={cn(
          'flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed px-4 py-5 transition-colors',
          uploading
            ? 'border-primary-300 bg-primary-50 dark:border-primary-600 dark:bg-primary-900/20'
            : 'border-gray-200 bg-gray-50/50 hover:border-primary-300 hover:bg-primary-50/50 dark:border-gray-600 dark:bg-gray-800/50 dark:hover:border-primary-600 dark:hover:bg-primary-900/10',
        )}
      >
        {uploading ? (
          <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
        ) : (
          <Upload className="h-6 w-6 text-gray-400" />
        )}
        <div className="text-center">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {uploading ? 'Wird hochgeladen...' : 'Datei hochladen'}
          </p>
          <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
            JPG, PNG, PDF, XLSX, DOCX – max. 10 MB
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,.xlsx,.docx"
          onChange={(e) => void handleUpload(e.target.files)}
          className="hidden"
          aria-label={t('ariaSelect')}
        />
      </div>
    </div>
  )
}
